import Uppy from "@uppy/core";
import XHRUpload from "@uppy/xhr-upload";
import { useUppyState } from "@uppy/react";
import { useEffect, useRef, useState } from "react";
import {
  agentAttachmentSchema,
  MAX_AGENT_ATTACHMENT_BYTES,
  MAX_AGENT_MESSAGE_ATTACHMENTS,
  type AgentAttachment,
} from "@opencloud/agent-protocol";
import { api, browserRequestId } from "./api.js";
import type { ChatUploads } from "./types.js";

type UploadStatus = {
  state: string;
  attachment?: AgentAttachment;
  messageId?: string | null;
};
type UploadBody = Record<string, unknown>;
type UploadMeta = Record<string, unknown>;
const draftKey = (scope: string) => `opencloud:chat-upload-drafts:${scope}`;

export function useChatUploads(
  scopeKey: string,
  initialPath: string | null,
  ensurePath: () => Promise<string>,
): ChatUploads {
  const [uppy] = useState(() =>
    new Uppy<UploadMeta, UploadBody>({
      autoProceed: false,
      restrictions: {
        maxFileSize: MAX_AGENT_ATTACHMENT_BYTES,
        maxNumberOfFiles: MAX_AGENT_MESSAGE_ATTACHMENTS,
      },
    }).use(XHRUpload, {
      method: "PUT",
      formData: false,
      limit: 3,
      withCredentials: true,
      endpoint: (file) => {
        if (Array.isArray(file) || typeof file.meta.endpoint !== "string")
          throw new Error("Upload is not prepared");
        return file.meta.endpoint;
      },
    }),
  );
  const files = useUppyState(uppy, (state) => Object.values(state.files));
  const [ready, setReady] = useState<AgentAttachment[]>([]);
  const [preparing, setPreparing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const restorePending = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const scope = useRef(initialPath);
  const currentKey = useRef(scopeKey);
  const generation = useRef(0);
  const prepare = useRef(false);
  const selected = useRef<AgentAttachment[]>([]);
  const awaitingSend = useRef(new Set<string>());
  const order = useRef<string[]>([]);
  const ensure = useRef(ensurePath);
  ensure.current = ensurePath;

  function save(next: AgentAttachment[]) {
    next.sort(
      (a, b) => order.current.indexOf(a.id) - order.current.indexOf(b.id),
    );
    selected.current = next;
    setReady(next);
    if (scope.current)
      try {
        localStorage.setItem(
          draftKey(scope.current),
          JSON.stringify([
            ...new Set([
              ...awaitingSend.current,
              ...next.map((file) => file.id),
            ]),
          ]),
        );
      } catch {
        /* Browser storage is optional. */
      }
  }
  function accept(id: string, status: UploadStatus) {
    const file = agentAttachmentSchema.parse(status.attachment);
    if (!status.messageId)
      save([...selected.current.filter((item) => item.id !== file.id), file]);
    uppy.removeFile(id);
  }

  useEffect(() => {
    if (
      currentKey.current !== scopeKey &&
      (!initialPath || initialPath !== scope.current)
    ) {
      generation.current++;
      uppy.cancelAll();
      selected.current = [];
      awaitingSend.current.clear();
      order.current = [];
      setReady([]);
      setPreparing(false);
      prepare.current = false;
      restorePending.current = false;
      setRestoring(false);
      scope.current = initialPath;
      setError(null);
    }
    currentKey.current = scopeKey;
    if (!scope.current && initialPath) scope.current = initialPath;
    const path = scope.current;
    if (!path || selected.current.length || uppy.getFiles().length) return;
    const token = generation.current;
    let ids: string[] = [];
    try {
      const stored: unknown = JSON.parse(
        localStorage.getItem(draftKey(path)) ?? "[]",
      );
      if (Array.isArray(stored)) {
        const all = stored.filter((id): id is string => typeof id === "string");
        ids = all.slice(0, MAX_AGENT_MESSAGE_ATTACHMENTS);
        awaitingSend.current = new Set(
          all.slice(MAX_AGENT_MESSAGE_ATTACHMENTS),
        );
      }
    } catch {
      /* No recoverable draft. */
    }
    if (!ids.length) return;
    order.current = ids;
    restorePending.current = true;
    setRestoring(true);
    void Promise.all(
      ids.map((id) =>
        api<UploadStatus>(
          `${path}/attachments/${encodeURIComponent(id)}`,
        ).catch(() => null),
      ),
    ).then((statuses) => {
      if (generation.current !== token) return;
      save(
        statuses.flatMap((status) => {
          const parsed = agentAttachmentSchema.safeParse(status?.attachment);
          return status?.state === "ready" &&
            !status.messageId &&
            parsed.success
            ? [parsed.data]
            : [];
        }),
      );
      restorePending.current = false;
      setRestoring(false);
    });
  }, [scopeKey, initialPath, uppy]);

  useEffect(() => {
    const success = (
      file: ReturnType<typeof uppy.getFiles>[number] | undefined,
      response: { body?: UploadBody },
    ) => {
      if (file) accept(file.id, response.body as UploadStatus);
    };
    uppy.on("upload-success", success);
    return () => {
      generation.current++;
      uppy.off("upload-success", success);
      uppy.cancelAll();
    };
  }, [uppy]);

  async function add(picked: File[]) {
    if (prepare.current || restorePending.current || !picked.length) return;
    setError(null);
    if (
      selected.current.length + uppy.getFiles().length + picked.length >
      MAX_AGENT_MESSAGE_ATTACHMENTS
    ) {
      setError("Attach up to 20 files per message.");
      return;
    }
    if (picked.some((file) => file.size > MAX_AGENT_ATTACHMENT_BYTES)) {
      setError("Each file can be up to 100 MB.");
      return;
    }
    const token = generation.current;
    prepare.current = true;
    setPreparing(true);
    try {
      const added = picked.map((file) => {
        const attachmentId = browserRequestId();
        order.current.push(attachmentId);
        return uppy.addFile({
          name: file.name || "Untitled",
          type: file.type,
          data: file,
          meta: { attachmentId, relativePath: attachmentId },
        });
      });
      const path = scope.current ?? (await ensure.current());
      if (generation.current !== token) return;
      scope.current = path;
      const batch = added.map((id) => uppy.getFile(id)!);
      await api(`${path}/attachments/uploads`, {
        method: "POST",
        body: JSON.stringify({
          files: batch.map((file) => ({
            id: file.meta.attachmentId,
            name: file.name,
            size: file.size,
            mediaType: file.type || "",
          })),
        }),
      });
      if (generation.current !== token) return;
      for (const file of batch)
        uppy.setFileMeta(file.id, {
          endpoint: `${path}/attachments/${file.meta.attachmentId}/content`,
        });
      void uppy.upload().catch((failure) => {
        if (generation.current === token)
          setError(
            failure instanceof Error
              ? failure.message
              : "Files could not be uploaded",
          );
      });
    } catch (failure) {
      if (generation.current === token)
        setError(
          failure instanceof Error
            ? failure.message
            : "Files could not be uploaded",
        );
    } finally {
      if (generation.current === token) {
        prepare.current = false;
        setPreparing(false);
      }
    }
  }

  async function retry(id: string) {
    const file = uppy.getFile(id);
    if (!file) return;
    const token = generation.current;
    try {
      const path = scope.current ?? (await ensure.current());
      if (generation.current !== token) return;
      scope.current = path;
      const status = await api<UploadStatus>(
        `${path}/attachments/${file.meta.attachmentId}`,
      ).catch(() => null);
      if (generation.current !== token) return;
      if (status?.state === "ready") {
        accept(id, status);
        return;
      }
      await api(`${path}/attachments/uploads`, {
        method: "POST",
        body: JSON.stringify({
          files: [
            {
              id: file.meta.attachmentId,
              name: file.name,
              size: file.size,
              mediaType: file.type || "",
            },
          ],
        }),
      });
      if (generation.current !== token) return;
      uppy.setFileMeta(id, {
        endpoint: `${path}/attachments/${file.meta.attachmentId}/content`,
      });
      await uppy.retryUpload(id);
    } catch (failure) {
      if (generation.current === token)
        setError(failure instanceof Error ? failure.message : "Retry failed");
    }
  }

  function remove(id: string) {
    const file = uppy.getFile(id);
    const attachmentId = file ? String(file.meta.attachmentId) : id;
    if (file) uppy.removeFile(id);
    save(selected.current.filter((item) => item.id !== id));
    if (scope.current)
      void api(`${scope.current}/attachments/${attachmentId}/upload`, {
        method: "DELETE",
      }).catch(() => undefined);
  }

  return {
    ready,
    preparing: preparing || restoring,
    error,
    add,
    retry,
    remove,
    items: [
      ...ready.map((file) => ({
        ...file,
        key: file.id,
        state: "done" as const,
        progress: 100,
      })),
      ...files.map((file) => ({
        id: String(file.meta.attachmentId),
        key: file.id,
        name: file.name,
        size: file.size ?? 0,
        state: file.error ? ("error" as const) : ("uploading" as const),
        progress: file.progress.percentage ?? 0,
      })),
    ],
    pending: preparing || restoring || files.length > 0,
    acknowledge: (
      attached: readonly AgentAttachment[],
      path = scope.current,
    ) => {
      const ids = new Set(attached.map((file) => file.id));
      if (path === scope.current) {
        for (const id of ids) awaitingSend.current.delete(id);
        save(selected.current.filter((file) => !ids.has(file.id)));
      } else if (path) {
        try {
          const stored: unknown = JSON.parse(
            localStorage.getItem(draftKey(path)) ?? "[]",
          );
          if (Array.isArray(stored))
            localStorage.setItem(
              draftKey(path),
              JSON.stringify(stored.filter((id) => !ids.has(id))),
            );
        } catch {
          /* A later reload can resolve accepted IDs through status. */
        }
      }
    },
    clear: (retainUntilAcknowledged = false) => {
      for (const file of selected.current) {
        if (retainUntilAcknowledged) awaitingSend.current.add(file.id);
        else awaitingSend.current.delete(file.id);
      }
      save([]);
      order.current = [];
      setError(null);
    },
  };
}
